// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Enterprise edition router.
 *
 * Extends the shared route base with enterprise-only routes:
 * landing page, registration, OAuth, password reset, email verification,
 * and credits.
 */

import { lazy } from 'react';
import { createRoute, createRouter, redirect } from '@tanstack/react-router';
import {
  rootRoute,
  authLoginRoute,
  createAppLayoutRoute,
  createAppRoutes,
  withSuspense,
} from './router-base';

// Enterprise-only eagerly loaded routes.
import { RegisterPage } from './routes/auth/register';
import { OAuthCallbackPage } from './routes/auth/oauth-callback';
import { VerifyEmailPage } from './routes/auth/verify-email';

// Enterprise-only lazy-loaded routes.
const LandingPage = lazy(() =>
  import('./routes/landing/index').then((m) => ({ default: m.LandingPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./routes/auth/forgot-password').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./routes/auth/reset-password').then((m) => ({ default: m.ResetPasswordPage })),
);
const CreditsPage = lazy(() =>
  import('./routes/credits/index').then((m) => ({ default: m.CreditsPage })),
);
const TermsPage = lazy(() =>
  import('./routes/legal/terms').then((m) => ({ default: m.TermsPage })),
);
const PrivacyPolicyPage = lazy(() =>
  import('./routes/legal/privacy').then((m) => ({ default: m.PrivacyPolicyPage })),
);

// ============================================================
// Enterprise app layout — adds email verification guard
// ============================================================

const appLayoutRoute = createAppLayoutRoute({
  onAuthenticated: ({ user }) => {
    if (user && user.emailVerified === false) {
      throw redirect({ to: '/auth/verify-email' as string as '/auth/login' });
    }
  },
});

// Create shared routes bound to the enterprise appLayoutRoute instance.
// This ensures getParentRoute references match the actual route tree.
const {
  dashboardRoute,
  profileRoute,
  experienceRoute,
  accomplishmentsRoute,
  skillsRoute,
  educationRoute,
  customSectionsRoute,
  customSectionEntriesRoute,
  applicationsRoute,
  applicationsNewRoute,
  applicationDetailRoute,
  promptsRoute,
  settingsProfileRoute,
  settingsAIProviderRoute,
  settingsStorageRoute,
  settingsTemplatesRoute,
  settingsDangerZoneRoute,
  settingsNotificationsRoute,
  settingsCVPreferencesRoute,
  importCvRoute,
} = createAppRoutes(appLayoutRoute);

// ============================================================
// Enterprise-only route definitions
// ============================================================

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: withSuspense(LandingPage),
});

const authRegisterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/register',
  component: RegisterPage,
});

const authForgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/forgot-password',
  component: withSuspense(ForgotPasswordPage),
});

const authResetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/reset-password',
  component: withSuspense(ResetPasswordPage),
});

const authVerifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/verify-email',
  component: VerifyEmailPage,
});

const authOAuthCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/oauth-callback',
  component: OAuthCallbackPage,
});

const creditsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/credits',
  component: withSuspense(CreditsPage),
});

const legalTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/terms',
  component: withSuspense(TermsPage),
});

const legalPrivacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/privacy',
  component: withSuspense(PrivacyPolicyPage),
});

// ============================================================
// Full route tree (shared + enterprise)
// ============================================================

const routeTree = rootRoute.addChildren([
  indexRoute,
  authLoginRoute,
  authRegisterRoute,
  authVerifyEmailRoute,
  authForgotPasswordRoute,
  authResetPasswordRoute,
  authOAuthCallbackRoute,
  legalTermsRoute,
  legalPrivacyRoute,
  appLayoutRoute.addChildren([
    dashboardRoute,
    profileRoute,
    experienceRoute,
    accomplishmentsRoute,
    skillsRoute,
    educationRoute,
    customSectionsRoute,
    customSectionEntriesRoute,
    applicationsRoute,
    applicationsNewRoute,
    applicationDetailRoute,
    creditsRoute,
    promptsRoute,
    settingsProfileRoute,
    settingsAIProviderRoute,
    settingsStorageRoute,
    settingsTemplatesRoute,
    settingsDangerZoneRoute,
    settingsNotificationsRoute,
    settingsCVPreferencesRoute,
    importCvRoute,
  ]),
]);

export const router = createRouter({ routeTree });

// Type-safe router declaration for TanStack Router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
